import { useParams, Link, useNavigate } from "react-router-dom";
import { useProvider } from "../../hooks/useProviders";
import { providerAPI } from "../../services/api";

const ProviderDetail = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { provider, loading, error } = useProvider(id);

	const handleDelete = async () => {
		if (
			window.confirm(`Are you sure you want to deactivate ${provider.name}?`)
		) {
			try {
				await providerAPI.delete(id);
				alert("Provider deactivated successfully");
				navigate("/providers");
			} catch (err) {
				alert(err.response?.data?.message || "Failed to delete provider");
			}
		}
	};

	if (loading) return <div className="loading">Loading provider...</div>;
	if (error) return <div className="error">Error: {error}</div>;
	if (!provider) return <div className="error">Provider not found</div>;

	const responseRate =
		provider.quotesRequested > 0
			? ((provider.quotesReceived / provider.quotesRequested) * 100).toFixed(1)
			: "0";

	const bookingRate =
		provider.quotesReceived > 0
			? ((provider.bookingsCompleted / provider.quotesReceived) * 100).toFixed(
					1,
				)
			: "0";

	return (
		<div className="provider-detail">
			<div className="page-header">
				<div>
					<h1>{provider.name}</h1>
					<div className="badges">
						<span className={`badge badge-${provider.type.toLowerCase()}`}>
							{provider.type}
						</span>
						<span className={`badge badge-${provider.status.toLowerCase()}`}>
							{provider.status}
						</span>
						{provider.tags &&
							provider.tags.map((tag, index) => (
								<span key={index} className="badge badge-tag">
									{tag}
								</span>
							))}
					</div>
				</div>
				<div className="header-actions">
					<Link to={`/providers/${id}/edit`} className="btn btn-primary">
						Edit
					</Link>
					{provider.status === "ACTIVE" && (
						<button onClick={handleDelete} className="btn btn-danger">
							Deactivate
						</button>
					)}
				</div>
			</div>

			<div className="detail-grid">
				<div className="detail-section">
					<h2>Contact Information</h2>
					<div className="info-grid">
						<div className="info-item">
							<label>Contact Name</label>
							<div>{provider.contactName || "N/A"}</div>
						</div>
						<div className="info-item">
							<label>Email</label>
							<div>
								<a href={`mailto:${provider.contactEmail}`}>
									{provider.contactEmail}
								</a>
							</div>
						</div>
						<div className="info-item">
							<label>Phone</label>
							<div>{provider.contactPhone || "N/A"}</div>
						</div>
						<div className="info-item">
							<label>Preferred Channel</label>
							<div>
								<span
									className={`badge badge-${provider.preferredChannel || "email"}`}
								>
									{{ email: "Email" }[provider.preferredChannel || "email"]}
								</span>
							</div>
						</div>
						{provider.phoneNumber && (
							<div className="info-item">
								<label>SMS Number</label>
								<div>{provider.phoneNumber}</div>
							</div>
						)}
					</div>
				</div>

				<div className="detail-section">
					<h2>Performance Metrics</h2>
					<div className="metrics-grid">
						<div className="metric-card">
							<div className="metric-value">
								{(provider.reliabilityScore * 100).toFixed(0)}%
							</div>
							<div className="metric-label">Reliability Score</div>
						</div>
						<div className="metric-card">
							<div className="metric-value">
								{Number(provider.averageResponseTime).toFixed(1)}h
							</div>
							<div className="metric-label">Avg Response Time</div>
						</div>
						<div className="metric-card">
							<div className="metric-value">{responseRate}%</div>
							<div className="metric-label">Response Rate</div>
						</div>
						<div className="metric-card">
							<div className="metric-value">{bookingRate}%</div>
							<div className="metric-label">Booking Success</div>
						</div>
					</div>
				</div>

				<div className="detail-section">
					<h2>Service Coverage</h2>

					<div className="subsection">
						<h3>Container Types</h3>
						<div className="container-types">
							{provider.containerTypes && provider.containerTypes.length > 0 ? (
								provider.containerTypes.map((type) => (
									<span key={type} className="badge badge-container">
										{type}
									</span>
								))
							) : (
								<span className="text-muted">No container types specified</span>
							)}
						</div>
					</div>

					<div className="subsection">
						<h3>Service Lanes</h3>
						<div className="lanes-list">
							{provider.primaryServiceLanes &&
							provider.primaryServiceLanes.length > 0 ? (
								provider.primaryServiceLanes.map((lane, index) => (
									<div key={index} className="lane-item">
										<span className="origin">{lane.originPort}</span>
										<span className="arrow">→</span>
										<span className="destination">{lane.destinationPort}</span>
									</div>
								))
							) : (
								<span className="text-muted">No service lanes specified</span>
							)}
						</div>
					</div>
				</div>

				<div className="detail-section">
					<h2>Activity Summary</h2>
					<div className="info-grid">
						<div className="info-item">
							<label>Quotes Requested</label>
							<div>{provider.quotesRequested}</div>
						</div>
						<div className="info-item">
							<label>Quotes Received</label>
							<div>{provider.quotesReceived}</div>
						</div>
						<div className="info-item">
							<label>Bookings Completed</label>
							<div>{provider.bookingsCompleted}</div>
						</div>
					</div>
				</div>

				{provider.destinationAddressesDelivered &&
					provider.destinationAddressesDelivered.length > 0 && (
						<div className="detail-section">
							<h2>Delivery History</h2>
							<div className="address-list">
								{provider.destinationAddressesDelivered.map(
									(address, index) => (
										<div key={index} className="address-item">
											{address}
										</div>
									),
								)}
							</div>
						</div>
					)}

				{provider.notes && (
					<div className="detail-section full-width">
						<h2>Notes</h2>
						<div className="notes-content">{provider.notes}</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ProviderDetail;
